import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { listenToUserData, upsertUserData } from "../redux/authSlice";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import countries, { CountryInfo } from "../GLOBAL/Countries";
import { useNavigate } from "react-router-dom";
import ReactDOM from "react-dom";
import logo from "../assets/app/logo.png";

/* ═══════════════════════════════════════════════════════════════════
   THEME: classy black & red, same tokens as the Shop and Profile pages
   ═══════════════════════════════════════════════════════════════════ */
const A = {
  ink: "#000", red: "#E10000", redDeep: "#B30000",
  text: "#000", textSoft: "#5C5C5C", muted: "#8A8A8A",
  sand: "#F4F4F4", line: "#E5E5E5", edge: "#D4D4D4",
  sans: "'Jost', system-ui, sans-serif",
  display: "'Cormorant Garamond', serif",
};

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Jost:wght@300;400;500;600&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Rounded:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200&display=block');

.material-symbols-rounded {
  font-family: 'Material Symbols Rounded';
  font-weight: normal; font-style: normal; line-height: 1; letter-spacing: normal;
  text-transform: none; display: inline-block; white-space: nowrap; word-wrap: normal;
  direction: ltr; -webkit-font-smoothing: antialiased; vertical-align: middle;
  font-variation-settings: 'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24;
}

.ep, .ep *, .ep *::before, .ep *::after,
.ep-country-dropdown, .ep-country-dropdown *, .ep-country-dropdown *::before, .ep-country-dropdown *::after { box-sizing:border-box; }
.ep, .ep-country-dropdown { font-family:'Jost', system-ui, sans-serif; }
.ep { color:${A.text}; background:#fff; min-height:100vh; -webkit-font-smoothing:antialiased; overflow-x:hidden; }
/* :where keeps this reset at zero specificity so every button class below wins */
:where(.ep) button { cursor:pointer; border:none; background:none; font-family:inherit; color:inherit; }
.ep button:focus-visible, .ep-country-dropdown input:focus-visible { outline:2px solid ${A.red}; outline-offset:3px; }

.ep-mono { font-size:11px; font-weight:500; letter-spacing:.2em; text-transform:uppercase; color:${A.textSoft}; }

/* ═══ TOP BAR ═══ */
.ep-top { display:flex; align-items:center; gap:16px; margin-bottom:30px; }
.ep-title { font-family:'Cormorant Garamond', serif; font-weight:500; font-size:clamp(38px,8vw,56px); line-height:1; margin:0; letter-spacing:.01em; }
.ep-spark { color:${A.red}; font-size:.4em; vertical-align:super; margin-left:6px; }
.ep-round {
  position:relative; width:46px; height:46px; border-radius:50%; flex-shrink:0;
  border:1px solid ${A.ink}; background:#fff; display:flex; align-items:center; justify-content:center;
  box-shadow:0 0 0 3px #fff, 0 0 0 4px ${A.ink}; transition:all .25s;
}
.ep-round:hover { border-color:${A.red}; color:${A.red}; box-shadow:0 0 0 3px #fff, 0 0 0 4px ${A.red}; }

/* ═══ CARDS: double border ═══ */
.ep-plate, .ep-section-card { position:relative; background:#fff; border:1px solid ${A.ink}; border-radius:26px; }
.ep-plate::after, .ep-section-card::after { content:""; position:absolute; inset:5px; border:1px solid ${A.ink}; border-radius:21px; pointer-events:none; }
.ep-section-card { padding:30px 28px; margin-bottom:20px; }

/* ═══ AVATAR ═══ */
.ep-avatar-wrap { position:relative; flex-shrink:0; }
.ep-avatar-ring { padding:4px; border-radius:50%; background:#fff; border:1px solid ${A.ink}; box-shadow:0 0 0 3px #fff, 0 0 0 4px ${A.ink}; display:inline-flex; }
.ep-avatar { display:block; border-radius:50%; object-fit:cover; background:${A.sand}; transition:opacity .2s; }
.ep-avatar.busy { opacity:.45; }
.ep-cam {
  position:absolute; bottom:2px; right:2px; width:32px; height:32px; border-radius:50%;
  background:${A.ink}; color:#fff; display:flex; align-items:center; justify-content:center; border:2px solid #fff;
  transition:background .2s, transform .2s;
}
.ep-avatar-wrap:hover .ep-cam { background:${A.red}; transform:scale(1.08); }
.ep-ver {
  position:absolute; top:0; left:0; width:26px; height:26px; border-radius:50%;
  background:${A.red}; color:#fff; display:flex; align-items:center; justify-content:center; border:2px solid #fff;
}

/* ═══ BADGE ═══ */
.ep-badge { display:inline-flex; align-items:center; gap:6px; padding:6px 13px; border-radius:999px; font-size:10px; font-weight:500; letter-spacing:.16em; text-transform:uppercase; }
.ep-badge.ok      { background:${A.ink}; color:#fff; }
.ep-badge.pending { border:1px solid #CFCFCF; color:${A.textSoft}; }

/* ═══ PROGRESS ═══ */
.ep-progress-track { height:4px; border-radius:999px; background:${A.line}; overflow:hidden; }
.ep-progress-fill  { height:100%; border-radius:999px; background:${A.red}; transition:width .6s ease; }

/* ═══ SECTION TABS ═══ */
.ep-tabs { display:flex; gap:8px; overflow-x:auto; scrollbar-width:none; padding:6px 4px; margin:0 -4px; -webkit-overflow-scrolling:touch; }
.ep-tabs::-webkit-scrollbar { display:none; }
.ep-tab {
  flex-shrink:0; height:38px; padding:0 18px; border-radius:999px; border:1px solid ${A.ink}; background:#fff;
  font-size:11.5px; font-weight:500; letter-spacing:.16em; text-transform:uppercase; white-space:nowrap; transition:all .2s;
}
.ep-tab:hover { border-color:${A.red}; color:${A.red}; }
.ep-tab.active { background:${A.ink}; color:#fff; border-color:${A.ink}; }
.ep-tab.active:hover { background:${A.red}; border-color:${A.red}; color:#fff; }

/* ═══ BUTTONS ═══ */
.ep-btn {
  flex:1; display:flex; align-items:center; justify-content:center; gap:9px; height:52px; padding:0 26px; border-radius:999px;
  border:1px solid ${A.ink}; font-size:11.5px; font-weight:500; letter-spacing:.22em; text-transform:uppercase;
  transition:background .25s, border-color .25s, color .25s, transform .25s;
}
.ep-btn.primary { background:${A.ink}; color:#fff; }
.ep-btn.primary:hover { background:${A.red}; border-color:${A.red}; transform:translateY(-1px); }
.ep-btn.primary:active { background:${A.redDeep}; border-color:${A.redDeep}; }
.ep-btn.disabled { background:#fff; border-color:#D9D9D9; color:#A3A3A3; cursor:not-allowed; }
.ep-btn.verify { background:#fff; color:${A.ink}; }
.ep-btn.verify:hover { border-color:${A.red}; color:${A.red}; transform:translateY(-1px); }
.ep-btn.verified { background:#fff; color:${A.ink}; cursor:default; }
.ep-btn.verified:disabled { opacity:1; cursor:default; }

/* ═══ SHARE BUTTON ═══ */
.ep-share-btn {
  width:48px; height:48px; flex-shrink:0; border-radius:50%; border:1px solid ${A.ink}; background:#fff;
  display:flex; align-items:center; justify-content:center; cursor:pointer; transition:all .25s;
}
.ep-share-btn:hover { border-color:${A.red}; color:${A.red}; transform:translateY(-1px); }

/* ═══ COUNTRY PICKER ═══ */
.ep-country-trigger {
  border-radius:14px; padding:22px 14px 16px 14px; cursor:pointer; background:#fff;
  user-select:none; display:flex; align-items:center; justify-content:space-between; min-height:70px;
  box-shadow: inset 0 0 0 1px ${A.edge}; transition: box-shadow .2s;
}
.ep-country-trigger:hover { box-shadow: inset 0 0 0 1px ${A.ink}; }
.ep-country-trigger.open  { box-shadow: inset 0 0 0 1.5px ${A.ink}, 0 0 0 3px rgba(0,0,0,.08); }
.ep-country-trigger.error { box-shadow: inset 0 0 0 1.5px ${A.red}; }
.ep-country-dropdown {
  background:#fff; border:1px solid ${A.ink}; border-radius:20px; box-shadow:0 24px 50px -20px rgba(0,0,0,.45);
  display:flex; flex-direction:column; max-height:280px; overflow:hidden; margin-top:6px;
}
.ep-country-search {
  width:100%; border:none; outline:none; border-radius:999px; padding:11px 16px; font-size:13px; font-family:'Jost',sans-serif; color:${A.text};
  background:#fff; box-shadow: inset 0 0 0 1px ${A.edge}; transition:box-shadow .2s;
}
.ep-country-search:focus { box-shadow: inset 0 0 0 1.5px ${A.ink}; }
.ep-country-row { padding:10px 14px; cursor:pointer; font-size:13px; font-family:'Jost',sans-serif; color:${A.text}; display:flex; align-items:center; gap:10px; border-radius:12px; transition: background .12s, color .12s; }
.ep-country-row:hover { background:${A.sand}; }
.ep-country-row.selected { color:${A.red}; font-weight:600; }

/* ═══ PHONE DIAL-CODE GROUP ═══ */
.ep-phone-group {
  display:flex; align-items:stretch; border-radius:14px; overflow:hidden; background:#fff;
  box-shadow: inset 0 0 0 1px ${A.edge}; transition: box-shadow .2s;
}
.ep-phone-group:hover   { box-shadow: inset 0 0 0 1px ${A.ink}; }
.ep-phone-group.focused { box-shadow: inset 0 0 0 1.5px ${A.ink}, 0 0 0 3px rgba(0,0,0,.08); }
.ep-phone-group.error   { box-shadow: inset 0 0 0 1.5px ${A.red}; }
.ep-phone-trigger {
  display:flex; align-items:center; gap:8px; padding:0 14px; cursor:pointer; flex-shrink:0;
  border-right:1px solid ${A.line}; font-weight:500; font-size:14px; color:${A.text};
}
.ep-phone-trigger .chev { font-size:9px; color:${A.muted}; }
.ep-phone-input-wrap { flex:1; min-width:0; }

/* round flag badge */
.ep-flag-sphere {
  position:relative; width:32px; height:32px; border-radius:50%; flex-shrink:0;
  display:flex; align-items:center; justify-content:center; font-size:16px; line-height:1;
  background:#fff; border:1px solid ${A.line}; overflow:hidden;
}

@media (max-width:600px) {
  .ep-section-card { padding:24px 20px; border-radius:22px; }
  .ep-section-card::after { border-radius:17px; }
  .ep-tab { padding:0 14px; font-size:10.5px; }
}
@media (prefers-reduced-motion: reduce) {
  .ep *, .ep *::before, .ep *::after { transition:none !important; animation:none !important; }
}
`;

// NOTE: "Billing" tab remains removed. iban, bic, taxId, vatNumber, bankName,
// and billingAddress are still not saved. "About" (userBio) is back per request.
// Email is a single unified `email` field.
const SECTIONS = [
  "Personal", "Contact", "Address", "Shipping", "Date of Birth", "About"
] as const;

/* ─── Google Material Symbols icon ─── */
const Icon: React.FC<{ name: string; size?: number; color?: string; style?: React.CSSProperties }> = ({ name, size = 20, color, style }) => (
  <span className="material-symbols-rounded" style={{ fontSize: size, color, ...style }}>{name}</span>
);

/* ─── Searchable Country Picker ─── */
interface CountryPickerProps {
  label: string;
  value: string;
  onChange: (name: string, code: string) => void;
  error?: boolean;
}

const CountryPicker: React.FC<CountryPickerProps> = ({ label, value, onChange, error }) => {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState("");
  const triggerRef = useRef<HTMLDivElement>(null);
  const searchRef  = useRef<HTMLInputElement>(null);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });

  const selectedCountry = countries.find(c => c.name === value);

  const filtered = countries.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.code.toLowerCase().includes(search.toLowerCase())
  );

  const openDropdown = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropPos({
        top:   rect.bottom + window.scrollY,
        left:  rect.left   + window.scrollX,
        width: rect.width,
      });
    }
    setOpen(true);
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  const handleSelect = (name: string, code: string) => {
    onChange(name, code);
    setSearch("");
    setOpen(false);
  };

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current && !triggerRef.current.contains(target)) {
        const dropdown = document.getElementById("country-dropdown-portal");
        if (dropdown && !dropdown.contains(target)) {
          setOpen(false);
          setSearch("");
        }
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const dropdown = open ? ReactDOM.createPortal(
    <div
      id="country-dropdown-portal"
      className="ep-country-dropdown"
      style={{
        position: "absolute",
        top:      dropPos.top,
        left:     dropPos.left,
        width:    dropPos.width,
        zIndex:   99999,
      }}
    >
      <div style={{ padding: "12px 12px 8px" }}>
        <input
          ref={searchRef}
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search country..."
          className="ep-country-search"
        />
      </div>
      <div style={{ overflowY: "auto", flex: 1, padding: "0 6px 8px" }}>
        {filtered.length === 0 ? (
          <div style={{ padding: "14px", textAlign: "center", color: A.muted, fontSize: 12 }}>
            No results
          </div>
        ) : filtered.map(c => (
          <div
            key={c.code}
            onMouseDown={() => handleSelect(c.name, c.code)}
            className={`ep-country-row${value === c.name ? " selected" : ""}`}
          >
            <span style={{ fontSize: 16 }}>{c.emoji}</span>
            <span style={{ flex: 1 }}>{c.name}</span>
            <span style={{ fontSize: 10, color: A.muted, letterSpacing: ".1em" }}>{c.code}</span>
          </div>
        ))}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div style={{ marginBottom: 16, position: "relative" }}>
      <div
        ref={triggerRef}
        onClick={openDropdown}
        className={`ep-country-trigger${open ? " open" : ""}${error ? " error" : ""}`}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={{
            fontSize: 9, color: open ? A.ink : A.textSoft,
            letterSpacing: "0.14em", fontFamily: A.sans,
            transition: "color 0.15s",
          }}>
            {label.toUpperCase()}
          </span>
          <span style={{
            fontSize: 14,
            color: selectedCountry ? A.text : A.muted,
            fontFamily: A.sans,
            display: "flex", alignItems: "center", gap: 6,
          }}>
            {selectedCountry
              ? <><span style={{ fontSize: 16 }}>{selectedCountry.emoji}</span> {selectedCountry.name}</>
              : "Select country"
            }
          </span>
        </div>
        <span style={{ color: A.muted, flexShrink: 0 }}>
          <Icon name={open ? "expand_less" : "expand_more"} size={18} color={A.muted} />
        </span>
      </div>

      {dropdown}
    </div>
  );
};

/* ─── Phone Number Field: dial-code picker grouped with the local number input ───
   The selected country's phoneCode is shown/stored separately from the digits the person
   types; they're concatenated only at save time (e.g. KE "+254" + "706143661" -> "254706143661"). */
const digitsOnly = (s: string | undefined | null) => (s || "").replace(/\D/g, "");

interface PhoneCodeFieldProps {
  label: string;
  country: CountryInfo | null;
  localValue: string;
  onChangeCountry: (c: CountryInfo) => void;
  onChangeLocal: (v: string) => void;
  error?: boolean;
}

const PhoneCodeField: React.FC<PhoneCodeFieldProps> = ({ label, country, localValue, onChangeCountry, onChangeLocal, error }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [focused, setFocused] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });

  const filtered = countries.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.phoneCode.includes(search)
  );

  const openDropdown = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, width: Math.max(260, rect.width) });
    }
    setOpen(true);
    setTimeout(() => searchRef.current?.focus(), 50);
  };

  const handleSelect = (c: CountryInfo) => {
    onChangeCountry(c);
    setSearch("");
    setOpen(false);
  };

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current && !triggerRef.current.contains(target)) {
        const dropdown = document.getElementById("phone-dropdown-portal");
        if (dropdown && !dropdown.contains(target)) { setOpen(false); setSearch(""); }
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const dropdown = open ? ReactDOM.createPortal(
    <div
      id="phone-dropdown-portal"
      className="ep-country-dropdown"
      style={{ position: "absolute", top: dropPos.top, left: dropPos.left, width: dropPos.width, zIndex: 99999 }}
    >
      <div style={{ padding: "12px 12px 8px" }}>
        <input
          ref={searchRef}
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search country or code..."
          className="ep-country-search"
        />
      </div>
      <div style={{ overflowY: "auto", flex: 1, padding: "0 6px 8px" }}>
        {filtered.length === 0 ? (
          <div style={{ padding: "14px", textAlign: "center", color: A.muted, fontSize: 12 }}>No results</div>
        ) : filtered.map(c => (
          <div
            key={c.code}
            onMouseDown={() => handleSelect(c)}
            className={`ep-country-row${country?.code === c.code ? " selected" : ""}`}
          >
            <span className="ep-flag-sphere">{c.emoji}</span>
            <span style={{ flex: 1 }}>{c.name}</span>
            <span style={{ fontSize: 11, color: A.muted, letterSpacing: ".06em" }}>{c.phoneCode}</span>
          </div>
        ))}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div style={{ marginBottom: 16, position: "relative" }}>
      <div className={`ep-phone-group${focused ? " focused" : ""}${error ? " error" : ""}`}>
        <div ref={triggerRef} className="ep-phone-trigger" onClick={openDropdown}>
          <span className="ep-flag-sphere">{country?.emoji || "🌐"}</span>
          <span>{country?.phoneCode || "+--"}</span>
          <span className="chev"><Icon name={open ? "expand_less" : "expand_more"} size={16} /></span>
        </div>
        <div className="ep-phone-input-wrap">
          <TextField
            label={label}
            value={localValue}
            onChange={e => onChangeLocal(e.target.value.replace(/[^\d\s-]/g, ""))}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            fullWidth
            placeholder="706143661"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 0, fontFamily: A.sans, fontSize: 14, color: A.text, background: "transparent",
                boxShadow: "none",
                "& fieldset": { border: "none" },
                "& .MuiOutlinedInput-input": { padding: "22px 14px" },
              },
              "& .MuiInputLabel-root": { fontFamily: A.sans, fontSize: 12, letterSpacing: "0.04em", color: A.textSoft, "&.Mui-focused": { color: A.ink } },
            }}
          />
        </div>
      </div>
      {country && (
        <Typography sx={{ fontFamily: A.sans, fontSize: 10, color: A.muted, mt: 0.75, ml: 0.5, letterSpacing: ".06em" }}>
          Saved as {country.phoneCode.replace("+", "")}{digitsOnly(localValue) || "…"}
        </Typography>
      )}
      {dropdown}
    </div>
  );
};

/* Resizes a picked photo to a small square-ish JPEG data URL (about 20-40 KB) so it can be
   stored straight on the user doc: no Storage bucket or API call needed. */
const fileToAvatar = (file: File, max = 320): Promise<string> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read that image.")); };
    img.src = url;
  });

/* ─── Main Component ─── */
const EditProfile: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [form, setForm]                 = useState<any>({});
  const [originalForm, setOriginalForm] = useState<any>({});
  const [birthDate, setBirthDate]       = useState<Date | null>(null);
  const [originalBirthDate, setOriginalBirthDate] = useState<Date | null>(null);
  const [uploading, setUploading]       = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [saved, setSaved]               = useState(false);
  const [activeSection, setActiveSection] = useState(0);

  const mainAddressRef     = useRef<HTMLInputElement>(null);
  const shippingAddressRef = useRef<HTMLInputElement>(null);

  // ── Contact Number: dial-code + local digits, concatenated only on save ──
  const [phoneCountry, setPhoneCountry] = useState<CountryInfo | null>(null);
  const [phoneLocal, setPhoneLocal]     = useState("");
  const [originalPhoneCountry, setOriginalPhoneCountry] = useState<CountryInfo | null>(null);
  const [originalPhoneLocal, setOriginalPhoneLocal]     = useState("");
  const phoneCountryManualRef = useRef(false); // true once the person picks a dial code themselves

  // ── Save active on ANY edit ──
  const hasChanged = JSON.stringify(form) !== JSON.stringify(originalForm)
    || (birthDate?.toISOString() || "") !== (originalBirthDate?.toISOString() || "")
    || phoneLocal !== originalPhoneLocal
    || phoneCountry?.code !== originalPhoneCountry?.code;
  const canSave = hasChanged;

  // live listener on the user doc; the returned function unsubscribes on unmount
  useEffect(() => { const unsub = dispatch(listenToUserData()); return () => unsub(); }, [dispatch]);

  useEffect(() => {
    if (user) {

      const data: any = {
        FirstName: user.firstName||"", MiddleName: user.middleName||"", LastName: user.lastName||"",
        username: user.username||"", email: user.email||"",
        contactNumber: user.phoneNumber||"", userBio: user.userBio||"",
        streetAddress: user.address?.streetAddress||"", apartmentNumber: user.address?.apartmentNumber||"",
        postalAddress: user.address?.postalAddress||"", city: user.address?.city||"",
        state: user.address?.state||"", country: user.address?.country||"",
        countryCode: user.address?.countryCode||"", zipCode: user.address?.zipCode||"",
        shippingStreetAddress: user.shippingAddress?.streetAddress||"",
        shippingApartmentNumber: user.shippingAddress?.apartmentNumber||"",
        shippingPostalAddress: user.shippingAddress?.postalAddress||"",
        shippingCity: user.shippingAddress?.city||"", shippingState: user.shippingAddress?.state||"",
        shippingCountry: user.shippingAddress?.country||"",
        shippingCountryCode: user.shippingAddress?.countryCode||"",
        shippingZipCode: user.shippingAddress?.zipCode||"",
        shippingMessage: user.shippingAddress?.shippingMessage||"",
      };
      setForm(data); setOriginalForm(data);
      const dob = user.birthDate ? new Date(user.birthDate) : null;
      setBirthDate(dob); setOriginalBirthDate(dob);

      // Work out which dial code the stored contactNumber already uses (longest match wins),
      // falling back to the person's address country, then to Kenya as a last resort.
      const rawPhone = digitsOnly(user.phoneNumber);
      const byLongestCode = [...countries].sort((a, b) => digitsOnly(b.phoneCode).length - digitsOnly(a.phoneCode).length);
      let resolved = rawPhone ? byLongestCode.find(c => rawPhone.startsWith(digitsOnly(c.phoneCode))) : undefined;
      let localDigits = resolved ? rawPhone.slice(digitsOnly(resolved.phoneCode).length) : rawPhone;
      if (!resolved) {
        resolved = countries.find(c => c.name === data.country) || countries.find(c => c.code === "KE");
        localDigits = rawPhone;
      }
      phoneCountryManualRef.current = false;
      setPhoneCountry(resolved || null); setOriginalPhoneCountry(resolved || null);
      setPhoneLocal(localDigits); setOriginalPhoneLocal(localDigits);
    }
  }, [user]);

  // Keep the dial code in sync with the residential address country, unless the
  // person has explicitly chosen a different one for their contact number.
  useEffect(() => {
    if (phoneCountryManualRef.current) return;
    if (!form.country) return;
    const match = countries.find(c => c.name === form.country);
    if (match && match.code !== phoneCountry?.code) setPhoneCountry(match);
  }, [form.country]);

  const set = (k: string, v: string) => setForm((p: any) => ({ ...p, [k]: v }));

  const selCountry = (name: string, code: string, prefix = "") => {
    setForm((p: any) => ({ ...p, [prefix + "country"]: name, [prefix + "countryCode"]: code }));
  };

  const handleShareProfile = async () => {
    const url = `${window.location.origin}/u/${form.profileLink || form.username}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Areluxe", text: "Check out my Areluxe profile", url });
      } else {
        await navigator.clipboard.writeText(url);
      }
    } catch {}
  };

  const allFields = ["FirstName","LastName","username","email",
    "streetAddress","apartmentNumber","postalAddress","city","country","countryCode",
    "shippingStreetAddress","shippingApartmentNumber","shippingPostalAddress","shippingCity",
    "shippingCountry","shippingCountryCode"];
  const filled     = allFields.filter(f => form[f]?.trim?.()).length + (birthDate ? 1 : 0) + (digitsOnly(phoneLocal) ? 1 : 0);
  const completion = Math.round((filled / (allFields.length + 2)) * 100);

  const handleSave = async () => {
    if (!canSave) return;
    // e.g. Kenya "+254" + typed "706143661" -> stored as "254706143661"
    const fullContactNumber = `${digitsOnly(phoneCountry?.phoneCode)}${digitsOnly(phoneLocal)}`;
    // Saved through upsertUserData onto users/{uid}. Email is not written: it belongs to
    // Firebase Auth, so changing it here would only desync the doc from the login.
    const payload: any = {
      firstName: form.FirstName,
      middleName: form.MiddleName,
      lastName: form.LastName,
      username: form.username,
      phoneNumber: fullContactNumber,
      birthDate: birthDate?.toISOString() || null,
      countryCode: form.countryCode,
      userBio: form.userBio,
      address: { streetAddress: form.streetAddress, apartmentNumber: form.apartmentNumber, postalAddress: form.postalAddress, city: form.city, state: form.state, country: form.country, countryCode: form.countryCode, zipCode: form.zipCode },
      shippingAddress: { streetAddress: form.shippingStreetAddress, apartmentNumber: form.shippingApartmentNumber, postalAddress: form.shippingPostalAddress, city: form.shippingCity, state: form.shippingState, country: form.shippingCountry, countryCode: form.shippingCountryCode, zipCode: form.shippingZipCode, shippingMessage: form.shippingMessage },
    };
    setSaving(true);
    setSaveError(null);
    const res = await dispatch(upsertUserData(payload));
    setSaving(false);
    if (upsertUserData.rejected.match(res)) {
      setSaveError((res.payload as string) || "Couldn't save your changes. Try again.");
      return;
    }
    setOriginalForm({ ...form, contactNumber: fullContactNumber }); setOriginalBirthDate(birthDate);
    setOriginalPhoneCountry(phoneCountry); setOriginalPhoneLocal(phoneLocal);
    setSaved(true); setTimeout(() => setSaved(false), 2500);
  };

  // Profile picture always falls back to the logo mark, no initials placeholder.
  const photo = user?.profilePicture || logo;

  return (
    <Box className="ep">
      <style>{CSS}</style>

      <Box sx={{ maxWidth: 720, mx: "auto", px: { xs: 2.5, sm: 4 }, pt: { xs: 4, sm: 7 }, pb: 12 }}>

        {/* Top bar */}
        <div className="ep-top">
          <button type="button" className="ep-round" aria-label="Back" onClick={() => navigate(-1)}>
            <Icon name="arrow_back" size={20} />
          </button>
          <h1 className="ep-title">Edit Profile<span className="ep-spark">✦</span></h1>
        </div>

        {/* Profile Card */}
        <Box className="ep-plate" sx={{ display: "flex", alignItems: "center", gap: 3, mb: 4, p: { xs: 2.5, sm: 3.5 } }}>
          <label htmlFor="uploadPhoto" style={{ cursor: "pointer", flexShrink: 0 }}>
            <div className="ep-avatar-wrap">
              <div className="ep-avatar-ring">
                <img
                  src={photo}
                  alt="avatar"
                  className={`ep-avatar${uploading ? " busy" : ""}`}
                  style={{ width: 90, height: 90 }}
                  onError={(e) => { (e.target as HTMLImageElement).src = logo; }}
                />
              </div>
              <div className="ep-cam"><Icon name="photo_camera" size={15} color="#fff" /></div>
              {user?.isVerified && <div className="ep-ver"><Icon name="check" size={14} color="#fff" /></div>}
            </div>
            <input id="uploadPhoto" type="file" accept="image/*" style={{ display: "none" }} onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setUploading(true);
              try {
                const dataUrl = await fileToAvatar(f);
                const res = await dispatch(upsertUserData({ profilePicture: dataUrl }));
                if (upsertUserData.rejected.match(res)) throw new Error("upload failed");
                setUploadSuccess(true);
                setTimeout(() => setUploadSuccess(false), 1500);
              } catch {
                setSaveError("Couldn't update your photo. Try a different image.");
              } finally {
                setUploading(false);
                e.target.value = "";
              }
            }} />
          </label>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: 28, sm: 34 }, fontWeight: 500, lineHeight: 1.05, color: A.text, fontFamily: A.display }}>
              {form.FirstName || "Your"} {form.LastName || "Name"}
            </Typography>
            <Typography sx={{ fontFamily: A.sans, fontSize: 13, color: A.textSoft, mt: 0.5, mb: 1.25, wordBreak: "break-all" }}>{form.email}</Typography>
            <span className={`ep-badge ${user?.isVerified ? "ok" : "pending"}`}>
              {user?.isVerified ? <Icon name="verified" size={13} /> : <Icon name="shield" size={13} />}
              {user?.isVerified ? "Verified" : "Unverified"}
            </span>
          </Box>
          {uploadSuccess && <Box sx={{ position: "absolute", top: 18, right: 20 }}><Icon name="check_circle" size={20} color={A.red} /></Box>}
        </Box>

        {/* Progress */}
        <Box sx={{ mb: 4, px: 0.5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
            <span className="ep-mono">Profile completion</span>
            <span style={{ fontSize: 22, fontWeight: 500, color: A.text, fontFamily: A.display, lineHeight: 1 }}>{completion}%</span>
          </Box>
          <div className="ep-progress-track"><div className="ep-progress-fill" style={{ width: completion + "%" }} /></div>
        </Box>

        {/* Section Nav */}
        <div className="ep-tabs" style={{ marginBottom: 20 }}>
          {SECTIONS.map((sec, i) => (
            <button
              type="button"
              key={sec}
              className={`ep-tab${activeSection === i ? " active" : ""}`}
              onClick={() => setActiveSection(i)}
            >
              {sec}
            </button>
          ))}
        </div>

        {/* ── PERSONAL ── */}
        {activeSection === 0 && (
          <div className="ep-section-card">
            <SHead t="Personal Information" s="Legal name as it appears on your identification documents." />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <LField label="Legal First Name" value={form.FirstName} onChange={v => set("FirstName", v)} />
              <LField label="Legal Middle Name" value={form.MiddleName} onChange={v => set("MiddleName", v)} />
              <LField label="Legal Last Name" value={form.LastName} onChange={v => set("LastName", v)} />

              <Box sx={{ position: "relative" }}>
                <LField label="Username" value={form.username} disabled />
                <Typography sx={{ fontFamily: A.sans, fontSize: 10, color: A.muted, mt: -1.5, mb: 1, letterSpacing: ".04em" }}>
                  Username changes are temporarily unavailable.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ flex: 1 }}>
                <LField
                  label="Profile Link"
                  value={form.username ? `areluxe.com/u/@${form.username}` : ""}
                  disabled
                />
              </Box>
              <div className="ep-share-btn" role="button" aria-label="Share profile" tabIndex={0} onClick={handleShareProfile}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleShareProfile(); }} style={{ marginBottom: 16 }}>
                <Icon name="share" size={18} />
              </div>
            </Box>
          </div>
        )}

        {/* ── CONTACT ── */}
        {activeSection === 1 && (
          <div className="ep-section-card">
            <SHead t="Contact Details" s="How we reach you. Pick your country code once and just type the local number." />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2, alignItems: "start" }}>
              <LField label="Email" value={form.email} disabled />
              <PhoneCodeField
                label="Contact Number"
                country={phoneCountry}
                localValue={phoneLocal}
                onChangeLocal={setPhoneLocal}
                onChangeCountry={(c) => { phoneCountryManualRef.current = true; setPhoneCountry(c); }}
              />
            </Box>
          </div>
        )}

        {/* ── ADDRESS ── */}
        {activeSection === 2 && (
          <div className="ep-section-card">
            <SHead t="Residential Address" s="Primary address for your account and correspondence." />
            <LField label="Street Address" value={form.streetAddress} onChange={v => set("streetAddress", v)} inputRef={mainAddressRef} />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <LField label="Apartment / Suite" value={form.apartmentNumber} onChange={v => set("apartmentNumber", v)} />
              <LField label="P.O. Box" value={form.postalAddress} onChange={v => set("postalAddress", v)} />
              <LField label="City" value={form.city} onChange={v => set("city", v)} />
              <LField label="State / Region" value={form.state} onChange={v => set("state", v)} />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <CountryPicker label="Country" value={form.country} onChange={(n, c) => selCountry(n, c)} />
              <LField label="Postal / Zip Code" value={form.zipCode} onChange={v => set("zipCode", v)} />
            </Box>
          </div>
        )}

        {/* ── SHIPPING ── */}
        {activeSection === 3 && (
          <div className="ep-section-card">
            <SHead t="Shipping Address" s="Delivery address for orders and physical cards." />
            <LField label="Street Address" value={form.shippingStreetAddress} onChange={v => set("shippingStreetAddress", v)} inputRef={shippingAddressRef} />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <LField label="Apartment / Suite" value={form.shippingApartmentNumber} onChange={v => set("shippingApartmentNumber", v)} />
              <LField label="P.O. Box" value={form.shippingPostalAddress} onChange={v => set("shippingPostalAddress", v)} />
              <LField label="City" value={form.shippingCity} onChange={v => set("shippingCity", v)} />
              <LField label="State / Region" value={form.shippingState} onChange={v => set("shippingState", v)} />
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <CountryPicker label="Country" value={form.shippingCountry} onChange={(n, c) => selCountry(n, c, "shipping")} />
              <LField label="Zip Code" value={form.shippingZipCode} onChange={v => set("shippingZipCode", v)} />
            </Box>
            <TextField
              multiline minRows={3}
              label="Delivery Instructions"
              placeholder="e.g. Leave with the doorman, gate code, preferred drop-off time…"
              value={form.shippingMessage || ""}
              onChange={e => set("shippingMessage", e.target.value)}
              fullWidth
              sx={fsx(false)}
            />
          </div>
        )}

        {/* ── DOB ── */}
        {activeSection === 4 && (
          <div className="ep-section-card">
            <SHead t="Date of Birth" s="Required for identity verification and regulatory compliance." />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker label="Date of Birth" value={birthDate} onChange={v => setBirthDate(v)} slotProps={{ textField: { fullWidth: true, sx: fsx(false) } }} />
            </LocalizationProvider>
          </div>
        )}

        {/* ── ABOUT ── */}
        {activeSection === 5 && (
          <div className="ep-section-card">
            <SHead t="About You" s="A short introduction visible on your public profile." />
            <TextField multiline minRows={5} label="Short Bio" value={form.userBio} onChange={e => set("userBio", e.target.value)} fullWidth sx={fsx(false)} />
          </div>
        )}

        {saveError && (
          <Typography sx={{ fontFamily: A.sans, fontSize: 13, color: A.red, mt: 2, textAlign: "center" }}>{saveError}</Typography>
        )}

        {/* ── ACTIONS ── */}
        <Box sx={{ mt: 3, display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
          <button
            type="button"
            className={`ep-btn ${canSave ? "primary" : "disabled"}`}
            disabled={!canSave}
            onClick={handleSave}
          >
            {saving ? "Saving..." : saved ? "Saved ✓" : "Save Changes"}
          </button>
          <button
            type="button"
            className={`ep-btn ${user?.isVerified ? "verified" : "verify"}`}
            disabled={user?.isVerified}
            onClick={() => !user?.isVerified && navigate("/Verification/" + user?.userId)}
          >
            {user?.isVerified ? "Verified ✓" : "Verify Account"}
          </button>
        </Box>
      </Box>
    </Box>
  );
};

export default EditProfile;

/* ── Sub-components ── */
const SHead: React.FC<{ t: string; s: string }> = ({ t, s }) => (
  <Box sx={{ mb: 3 }}>
    <Typography sx={{ fontSize: 30, fontWeight: 500, lineHeight: 1.05, color: A.text, fontFamily: A.display, mb: 0.75 }}>{t}</Typography>
    <Typography sx={{ fontFamily: A.sans, fontSize: 13, color: A.textSoft, lineHeight: 1.6 }}>{s}</Typography>
  </Box>
);

interface LFieldProps {
  label: string; value: string; onChange?: (v: string) => void;
  disabled?: boolean; error?: boolean; required?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
}
const LField: React.FC<LFieldProps> = ({ label, value, onChange, disabled, error, required, inputRef }) => (
  <TextField label={label} value={value || ""} onChange={e => onChange?.(e.target.value)} fullWidth disabled={disabled} inputRef={inputRef} required={required} sx={fsx(error)} />
);

/* MUI TextField default sizing/variant is untouched. Only the skin (hairline
   border, rounded radius, black focus ring) is restyled to match the classy theme. */
const fsx = (hasError?: boolean) => ({
  mb: 2,
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px", fontFamily: A.sans, fontSize: 14, color: A.text, background: "#fff",
    boxShadow: `inset 0 0 0 1px ${A.edge}`,
    transition: "box-shadow .2s",
    "& fieldset": { border: "none" },
    "& .MuiOutlinedInput-input": { padding: "22px 14px" },
    "& textarea.MuiOutlinedInput-input": { padding: "6px 2px" },
    "&:hover": { boxShadow: `inset 0 0 0 1px ${A.ink}` },
    "&.Mui-focused": { boxShadow: `inset 0 0 0 1.5px ${A.ink}, 0 0 0 3px rgba(0,0,0,.08)` },
    "&.Mui-error": { boxShadow: `inset 0 0 0 1.5px ${A.red}` },
    "&.Mui-disabled": { background: "#F6F6F6", boxShadow: `inset 0 0 0 1px ${A.line}` },
  },
  "& .MuiInputLabel-root": {
    fontFamily: A.sans, fontSize: 12, letterSpacing: "0.04em", color: A.textSoft,
    "&.Mui-focused": { color: A.ink },
    "&.Mui-error": { color: A.red },
  },
  "& .MuiFormHelperText-root": { fontFamily: A.sans, fontSize: 10 },
  ...(hasError ? {} : {}),
});